import Alpine from 'alpinejs';
import {
    handleAuthenticationRequiredResponse,
    readJsonResponse
} from '../../utils/authResponse.js';
import { getCsrfToken } from '../../utils/helpers.js';

Alpine.store('itineraryShare', {
    shareUrl: '',
    reservationId: '',
    title: '',
    dates: '',
    location: '',
    emails: '',
    copied: false,
    sending: false,
    error: '',
    success: false,
    // Retains one unsent draft for an explicit retry of the same itinerary.
    retryDraft: null,

    open() {
        const el = document.querySelector('[data-itinerary-share]');
        if (!el) {
            console.warn('itineraryShare: data-itinerary-share not found');
            return;
        }

        const reservationId =
            el.getAttribute('data-itinerary-share-reservation-id') || '';
        // Consume the retry once and only for its original reservation so a
        // recipient draft cannot surface in another itinerary.
        const retryDraft =
            this.retryDraft?.reservationId === reservationId
                ? this.retryDraft
                : null;

        this.retryDraft = null;
        this.shareUrl = el.getAttribute('data-itinerary-share-url') || '';
        this.reservationId = reservationId;
        this.title = el.getAttribute('data-itinerary-share-title') || '';
        this.dates = el.getAttribute('data-itinerary-share-dates') || '';
        this.location = el.getAttribute('data-itinerary-share-location') || '';
        this.copied = false;
        this.error = '';
        this.success = false;
        this.emails = retryDraft?.emails || '';

        Alpine.store('modals').open('share-itinerary-modal');
    },

    close() {
        Alpine.store('modals').close('share-itinerary-modal');
    },

    async copyLink() {
        if (!this.shareUrl) return;
        try {
            await navigator.clipboard.writeText(this.shareUrl);
        } catch {
            const input = document.createElement('input');
            input.value = this.shareUrl;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
        }
        this.copied = true;
        setTimeout(() => {
            this.copied = false;
        }, 2000);
    },

    async sendEmail() {
        this.error = '';
        this.success = false;

        if (!this.reservationId || !this.emails) {
            this.error = 'Please enter at least one email address.';
            return;
        }

        // Refresh before sending so an expired session reaches the structured
        // auth response instead of failing stale-CSRF validation first.
        const csrf = await getCsrfToken(true);
        if (!csrf || !csrf.name || !csrf.value) {
            this.error =
                "We couldn't verify your request. Please refresh the page and try again.";
            return;
        }

        const formData = new FormData();
        formData.append('reservationId', this.reservationId);
        formData.append('emails', this.emails);
        formData.append(csrf.name, csrf.value);

        this.sending = true;

        try {
            const response = await fetch('/actions/avari/itinerary/send', {
                method: 'POST',
                body: formData,
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });
            const data = await readJsonResponse(response);

            if (
                handleAuthenticationRequiredResponse(response, data, {
                    userStore: Alpine.store('user'),
                    modalsStore: Alpine.store('modals')
                })
            ) {
                // Preserve the draft for one explicit reopen, but never resend
                // the itinerary automatically after login.
                this.retryDraft = {
                    reservationId: this.reservationId,
                    emails: this.emails
                };
                // Reset the active model so reopening produces a reactive
                // change that repopulates the input after the dialogs close.
                this.emails = '';
                return;
            }

            if (!data) {
                throw new Error('Itinerary API returned an invalid response');
            }

            if (response.ok && data.success) {
                this.success = true;
                this.emails = '';
            } else {
                this.error =
                    data.error ||
                    "We couldn't send this itinerary. Please try again.";
            }
        } catch {
            this.error = "We couldn't send this itinerary. Please try again.";
        } finally {
            this.sending = false;
        }
    }
});
